// Libraries
import React from "react";
import ReactDOM from "react-dom/client";
import { SnackbarProvider, SnackbarProviderProps } from "notistack";

// Icons
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import DoneIcon from "@mui/icons-material/Done";

// React Query
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
// Redux
import { Provider } from "react-redux";
import { store } from "@/redux/store";

// MUI
import { LicenseInfo } from "@mui/x-license";

// Context
import { SubscriptionsProvider } from "@/Common/context/subscriptions/Subscriptions";

// Layouts
import { AUTO_HIDE_DURATION, MAX_SNACK, SnackbarsCustomStyle } from "./Layouts/components/Snackbars";

// Components
import App from "./App";

// Styles
import "./index.css";
import "../node_modules/flag-icons/css/flag-icons.min.css";
import { SelectionPayrollClosingProvider } from "./Washouts/context/selectionPayrollClosingContext";
import { ThemeProvider } from "@/themeContext";

LicenseInfo.setLicenseKey(import.meta.env.VITE_APP_MATERIAL_LICENSE_KEY ?? "");

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			gcTime: 60000,
			staleTime: 60000,
			retry: 1,
		},
	},
});

const snackBarProps: SnackbarProviderProps = {
	maxSnack: MAX_SNACK,
	anchorOrigin: { vertical: "bottom", horizontal: "right" },
	autoHideDuration: AUTO_HIDE_DURATION,
	iconVariant: {
		error: <ErrorOutlineRoundedIcon fontSize="small" className="mr-2" />,
		success: <DoneIcon fontSize="small" className="mr-2" />,
	},
	Components: {
		error: SnackbarsCustomStyle,
		success: SnackbarsCustomStyle,
	},
};

const rootElement = document.getElementById("root");
if (rootElement) {
	ReactDOM.createRoot(rootElement).render(
		<React.StrictMode>
			<ThemeProvider>
				<QueryClientProvider client={queryClient}>
					<SubscriptionsProvider maxSubscriptions={Number(import.meta.env.VITE_APP_MAX_SUBSCRIPTIONS) || 50}>
						<Provider store={store}>
							<SelectionPayrollClosingProvider>
								<SnackbarProvider {...snackBarProps}>
								<App />
								</SnackbarProvider>
							</SelectionPayrollClosingProvider>
						</Provider>
					</SubscriptionsProvider>
					<ReactQueryDevtools initialIsOpen={false} />
				</QueryClientProvider>
			</ThemeProvider>
		</React.StrictMode>
	);
}
